"use client";

import { useSyncExternalStore } from "react";
import { isCroppable } from "@/lib/image-crop";

/*
 * ONE CROP AT A TIME, ASKED FOR FROM ANYWHERE.
 *
 * Every picture slot calls `await requestCrop(file, { aspect })` between the
 * pick and the upload, and gets back the cropped file — or null when the
 * reader cancelled. The dialog itself is mounted once (`ImageCropHost`, in
 * ClientLayout), so a form hook needs no markup of its own for it, and the same
 * dialog opens over a page or over another dialog (the new-collection one).
 * The store is the toast store's shape: an external value and its listeners.
 */

export type CropOptions = {
  /** The slot's shape, width ÷ height. Omitted: the reader chooses — the picture as it is, or a preset. */
  aspect?: number;
  /** A portrait shows as a circle. */
  round?: boolean;
};
export type CropRequest = CropOptions & {
  id: number;
  file: File;
  url: string;
  resolve: (file: File | null) => void;
};
let current: CropRequest | null = null;
let seq = 0;
const subs = new Set<() => void>();
const emit = () => subs.forEach(f => f());

/**
 * Ask the reader to crop `file`. A file the crop does not take (a video, an
 * animated GIF, an SVG) comes straight back as it is.
 */
export function requestCrop(file: File, options: CropOptions = {}): Promise<File | null> {
  if (typeof window === "undefined" || !isCroppable(file.type)) return Promise.resolve(file);
  // A second request while one is open (a re-pick) replaces it: the first one is cancelled.
  if (current) settle(null);
  return new Promise(resolve => {
    current = {
      ...options,
      id: ++seq,
      file,
      url: URL.createObjectURL(file),
      resolve
    };
    emit();
  });
}

/** Close the open request with its answer. */
export function settle(file: File | null) {
  const req = current;
  if (!req) return;
  current = null;
  URL.revokeObjectURL(req.url);
  req.resolve(file);
  emit();
}
const subscribe = (cb: () => void) => {
  subs.add(cb);
  return () => void subs.delete(cb);
};
const getSnapshot = () => current;
const getServerSnapshot = () => null;
export function useCropRequest() {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}